import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

import EmptyState from '@/components/ui/EmptyState'
import MenuItem, { ROW, type MenuItemSkin, type MenuOption } from '@/components/ui/MenuItem'
import Popover, { type PopoverTriggerProps } from '@/components/ui/Popover'
import SearchInput from '@/components/ui/SearchInput'
import Sheet from '@/components/ui/Sheet'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'

/** How long a typeahead run stays open before the next letter starts a new one. */
const TYPEAHEAD_MS = 600

/** A command under the list — "Create a profile" — rather than one more value to pick. */
interface MenuSingleAction {
  label: string
  /**
   * Decorative: pass it `aria-hidden`. The label is the row's whole name. Mark a + `data-turn` and
   * it turns 90° on the row's hover (rest:kit, `glyph-turn`).
   */
  icon?: ReactNode
  onSelect: () => void
}

interface MenuSingleProps<T extends string> {
  options: readonly MenuOption<T>[]
  value: T | null
  onChange: (next: T) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Renders the anchor. Spread the supplied props onto a SelectTrigger. */
  trigger: (props: PopoverTriggerProps) => ReactNode
  /** Names the list for assistive tech — "Broker", "Rows per page". */
  label: string
  /** A field above the list. Off for a list short enough to read at a glance. */
  searchable?: boolean
  searchPlaceholder?: string
  align?: 'start' | 'end'
  /** Panel width. The frames draw 260px for a broker list, 92px for page sizes. */
  className?: string
  /** One row below a divider, outside the listbox and outside the search. */
  action?: MenuSingleAction
  /** The rows' look; see `MenuItemSkin`. Default `tick`. */
  skin?: MenuItemSkin
  /**
   * The panel's look above `lg` (sz:kit). `default`: Popover's own (subtle hairline, nav shadow).
   * `select`: a select's open menu in the mock (`stc-select`, `sta-menu`): the default hairline and
   * a 0 14 18 drop (`--shadow-select-menu`). Pair it with `align="end"` and `skin="plain"` for the
   * stock page's FY / Q menus. The sheet below `lg` is the same either way.
   */
  panel?: 'default' | 'select'
  /** Opens upward when it fits (Popover `prefer`, rest:al). Default `auto`. */
  prefer?: 'auto' | 'above'
}

const PANEL_SKIN = {
  default: null,
  select: 'border-border shadow-(--shadow-select-menu)',
} as const

/**
 * Pick one of a list, in a panel below `lg` — a bottom sheet — and an anchored
 * popover above it.
 *
 * A listbox rather than a native `<select>` because a `<select>`'s open list is
 * OS chrome no stylesheet reaches — on this palette it renders white with the
 * system highlight, which is the one thing the frames never draw. So the test
 * is whether the design draws a panel of its own, not whether the list is long
 * enough to need searching: `Pagination` opens a drawn 92px panel over four
 * numbers and passes `searchable={false}`. The search field is one more thing
 * only a listbox can offer, not the reason to reach for one. SelectChip is the
 * native picker, for a control the frames leave in OS chrome.
 *
 * Focus stays on the search field and the highlight moves through the list via
 * `aria-activedescendant`. Moving real focus into the list instead would take
 * the caret out of the field the member is still typing in.
 *
 * Typeahead is only wired up when there is no search field. With one, typing a
 * letter already jumps to matching options — that IS the search.
 *
 * `action` is a real `<button>` below the list, never an option inside it. As
 * an option, a command is announced as one more value to choose, is drawn like
 * one, and is filtered away by a search that matches nothing — exactly when a
 * member typing a name that doesn't exist yet needs "Create" most. Tab reaches
 * it; the arrow keys stay on the options, so the listbox's
 * `aria-activedescendant` never has to name something that isn't in it.
 *
 * The shell is chosen from a media query, not from the user agent: a desktop
 * window dragged narrow is the same 390px as a phone, and the frames draw the
 * sheet for the width rather than for the device. Every piece of state below
 * lives here, above that choice, so crossing the breakpoint mid-open swaps the
 * shell and keeps the query and the highlight.
 */
export default function MenuSingle<T extends string>({
  options,
  value,
  onChange,
  open,
  onOpenChange,
  trigger,
  label,
  searchable = true,
  searchPlaceholder = 'Search',
  align = 'start',
  className,
  action,
  skin = 'tick',
  panel = 'default',
  prefer = 'auto',
}: MenuSingleProps<T>) {
  const listId = useId()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  const typed = useRef({ run: '', at: 0 })

  const desktop = useIsDesktop()
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  // Adjusting state while a prop changes, rather than in an effect: the list
  // must already be filtered and pointed at the current value on the first
  // frame the panel is painted, not one frame later.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setQuery('')
      setActiveIndex(
        Math.max(
          options.findIndex((option) => option.value === value),
          0,
        ),
      )
    }
  }

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (needle === '') return options
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        option.keywords?.toLowerCase().includes(needle) === true,
    )
  }, [options, query])

  const move = (index: number) => {
    if (filtered.length === 0) return
    const next = (index + filtered.length) % filtered.length
    setActiveIndex(next)
    itemRefs.current[next]?.scrollIntoView({ block: 'nearest' })
  }

  const commit = (option: MenuOption<T>) => {
    if (option.disabled === true) return
    onChange(option.value)
    onOpenChange(false)
  }

  const typeahead = (key: string) => {
    const now = Date.now()
    const run = now - typed.current.at > TYPEAHEAD_MS ? key : typed.current.run + key
    typed.current = { run, at: now }

    // A repeated single letter cycles through the options starting with it;
    // a longer run keeps narrowing from where the highlight already sits.
    const from = run.length === 1 ? activeIndex + 1 : activeIndex
    const needle = run.toLowerCase()
    for (let step = 0; step < filtered.length; step += 1) {
      const index = (from + step) % filtered.length
      const option = filtered[index]
      if (option?.disabled !== true && option?.label.toLowerCase().startsWith(needle) === true) {
        move(index)
        return
      }
    }
  }

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      move(activeIndex + 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      move(activeIndex - 1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      move(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      move(filtered.length - 1)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const option = filtered[activeIndex]
      if (option) commit(option)
    } else if (!searchable && event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
      event.preventDefault()
      typeahead(event.key)
    }
  }

  const activeId = filtered[activeIndex] ? `${listId}-${String(activeIndex)}` : undefined

  const body = (
    <>
      {searchable ? (
        <SearchInput
          ref={inputRef}
          aria-label={`Search ${label}`}
          aria-controls={listId}
          aria-activedescendant={activeId}
          placeholder={searchPlaceholder}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setActiveIndex(0)
          }}
          onKeyDown={onKeyDown}
          className="shrink-0 rounded-sm px-3"
        />
      ) : null}

      <div
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={label}
        tabIndex={searchable ? -1 : 0}
        aria-activedescendant={searchable ? undefined : activeId}
        onKeyDown={searchable ? undefined : onKeyDown}
        className={cn(
          // `flex-auto`, not `flex-1`: this is a direct child of Popover's
          // panel (desktop) or Sheet's body (mobile), both sized from their
          // content with only a max-height cap. `flex-1`'s zero flex-basis
          // reads as zero content to WebKit's fit-content column sizing and
          // collapses the panel — see Modal.tsx.
          'flex min-h-0 flex-auto flex-col gap-1 overflow-y-auto overscroll-y-none focus:outline-none',
          // The popover's own `gap-1` already puts 4px under the field, so
          // `mt-1` makes it 8; the sheet's content column has no gap, so the
          // whole 8px is asked for.
          searchable ? (desktop ? 'mt-1' : 'mt-2') : null,
        )}
      >
        {filtered.map((option, index) => (
          <MenuItem
            key={option.value}
            ref={(node) => {
              itemRefs.current[index] = node
            }}
            kind="single"
            id={`${listId}-${String(index)}`}
            label={option.label}
            count={option.count}
            disabled={option.disabled}
            selected={option.value === value}
            active={index === activeIndex}
            skin={skin}
            onSelect={() => {
              commit(option)
            }}
          />
        ))}
      </div>
      {filtered.length === 0 ? (
        <EmptyState
          kind="no-results"
          size="inline"
          query={query}
          description={null}
          className="px-3 py-2"
        />
      ) : null}
      {action ? (
        // The popover's own `gap-1` puts 4px above this; the sheet's content
        // column has no gap, so `mt-1` adds it. Either way the hairline sits
        // 4px from the rows on each side, the gap they keep between themselves.
        <div className={cn('flex shrink-0 flex-col gap-1', desktop ? null : 'mt-1')}>
          <div className="h-px bg-border" />
          <button
            type="button"
            onClick={() => {
              action.onSelect()
              onOpenChange(false)
            }}
            className={cn(
              ROW,
              // MenuActionItem's inset ring: an outward one would paint over
              // the hairline, and be clipped by the sheet's scrolling body.
              'cursor-pointer gap-2 text-popover-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
              // rest:kit — the action row hovers as the rows above it do: the tooltip's step-up
              // (#202020) under `tickLift` and `plain`, the inset grey under `tick`; an icon marked
              // `data-turn` (a +) turns 90° (mock Portfolio switcher's "Create a profile").
              skin === 'tick' ? 'hover:bg-muted' : 'hover:bg-tooltip',
              'glyph-turn',
            )}
          >
            {action.icon}
            <span className="min-w-0 flex-1 truncate" title={action.label}>
              {action.label}
            </span>
          </button>
        </div>
      ) : null}
    </>
  )

  if (!desktop) {
    return (
      <Sheet
        open={open}
        onOpenChange={onOpenChange}
        trigger={trigger}
        initialFocusRef={searchable ? inputRef : listRef}
        title={label}
      >
        {body}
      </Sheet>
    )
  }

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      initialFocusRef={searchable ? inputRef : listRef}
      align={align}
      prefer={prefer}
      hasPopup="listbox"
      className={cn('w-65', PANEL_SKIN[panel], className)}
    >
      {body}
    </Popover>
  )
}
